import React, { useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastifyAlert } from "../../CommonComponents/ToastifyAlert";
import { useAuthDetails } from "../../Common/cookiesHelper";
import { useSelector } from "react-redux";
import { PERMISSIONS } from "../../Constants/permission";
import printPage from "../../helperFunctions/printPage";
import {
  resolveStocktakeReasonLabel,
  getCountEntryStatus,
  stFmtSigned,
} from "../NewStocktake/app/lib/stocktakeModel";
import "./StocktakeReportPrint.css";

// One source of truth for header / <col> widths / body cells, so the three can
// never drift apart when the Δ $ column is gated off.
// `width` is used when Δ $ prints, `widthNoCost` when it is hidden.
const COLUMNS = [
  { key: "item", label: "Item", width: "25%", widthNoCost: "28%" },
  { key: "sku", label: "SKU", width: "14%", widthNoCost: "15%" },
  { key: "expected", label: "Expected", width: "9%", widthNoCost: "10%", num: true },
  { key: "counted", label: "Counted", width: "9%", widthNoCost: "10%", num: true },
  { key: "delta", label: "Δ Units", width: "9%", widthNoCost: "10%", num: true },
  {
    key: "deltaCost",
    label: "Δ $",
    width: "10%",
    widthNoCost: "0%",
    num: true,
    // The Android WebView strips this column from its own PDF render.
    extraClass: "hide-total-discrepancy-cost-for-android",
  },
  { key: "status", label: "Status", width: "11%", widthNoCost: "12%" },
  { key: "reason", label: "Reason", width: "13%", widthNoCost: "15%" },
];

const StocktakeReportPrint = () => {
  const { LoginGetDashBoardRecordJson } = useAuthDetails();
  const merchantData = LoginGetDashBoardRecordJson?.data;
  const storenameCookie =
    merchantData?.name ||
    merchantData?.business_name ||
    merchantData?.store_name ||
    "";
  const storeAddress = useMemo(() => {
    if (!merchantData) return "";
    const line1 = String(
      merchantData?.a_address_line_1 ?? merchantData?.address_line_1 ?? "",
    ).trim();
    const line2 = String(
      merchantData?.a_address_line_2 ?? merchantData?.address_line_2 ?? "",
    ).trim();
    const city = String(merchantData?.a_city ?? merchantData?.city ?? "").trim();
    const state = String(
      merchantData?.a_state ?? merchantData?.state ?? "",
    ).trim();
    const zip = String(merchantData?.a_zip ?? merchantData?.zip ?? "").trim();
    const street = [line1, line2].filter(Boolean).join(" ");
    const cityStateZip = [city, state, zip].filter(Boolean).join(" ");
    const structured = [street, cityStateZip].filter(Boolean).join(", ");
    if (structured) return structured;
    return String(
      merchantData?.address ||
        merchantData?.business_address ||
        merchantData?.store_address ||
        "",
    ).trim();
  }, [merchantData]);
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();
  const { permissions } = useSelector((state) => state.permissions);

  const singleStocktakeState = useMemo(() => {
    return location?.state?.data;
  }, [location]);

  // Fire the print only once the custom fonts have actually loaded and the
  // browser has laid the table out — a blind timeout used to race that and gave
  // an intermittently broken first page.
  useEffect(() => {
    if (!singleStocktakeState) {
      ToastifyAlert("Sorry, couldn't find Stocktake details!", "error");

      return;
    }

    let cancelled = false;
    const fire = () => {
      if (cancelled || !printRef.current) return;
      printPage();
      navigate(-1);
    };

    // Never let a stalled font load swallow the print — cap the wait.
    const fontsReady = Promise.race([
      typeof document !== "undefined" && document.fonts?.ready
        ? document.fonts.ready
        : Promise.resolve(),
      new Promise((resolve) => setTimeout(resolve, 1200)),
    ]);

    let timerId;
    fontsReady
      .catch(() => {})
      .then(() => {
        if (cancelled) return;
        // two frames = layout + paint settled, then a short guard for images
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            timerId = setTimeout(fire, 150);
          });
        });
      });

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [singleStocktakeState, navigate]);

  // Callers may pass their own cost visibility (Stocktake App session applies
  // posted-only + Hide Cost rules); otherwise fall back to the HC permission.
  const hideDiscrepancyCost =
    Boolean(location?.state?.hideCost) ||
    (permissions.length > 0 && permissions.includes(PERMISSIONS.HIDE_COST));

  const columns = useMemo(
    () =>
      hideDiscrepancyCost
        ? COLUMNS.filter((column) => column.key !== "deltaCost")
        : COLUMNS,
    [hideDiscrepancyCost],
  );

  const formatDeltaUnits = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    return stFmtSigned(n);
  };

  const getPrintItemStatus = (item) => {
    if (item.status) return item.status;
    const counted = item.counted ?? item.new_qty;
    if (counted === "" || counted === null || counted === undefined) {
      return "Pending";
    }
    return getCountEntryStatus({
      counted,
      expected: item.expected ?? item.current_qty ?? 0,
    });
  };

  const buildRowCells = (item) => {
    const counted = item.counted ?? item.new_qty;
    const isCounted =
      counted !== "" && counted !== null && counted !== undefined;
    return {
      item: item.product_name || item.name || "",
      sku: item.sku || item.upc || "",
      expected: item.expected ?? item.current_qty ?? "",
      counted: isCounted ? counted : "",
      delta: isCounted ? formatDeltaUnits(item.discrepancy) : "",
      deltaCost: isCounted ? item.discrepancy_cost : "",
      status: getPrintItemStatus(item),
      reason: resolveStocktakeReasonLabel(item) || "",
    };
  };

  const items = singleStocktakeState?.stocktake_item ?? [];

  const totalCells = {
    item: "Total Discrepancy",
    delta: formatDeltaUnits(singleStocktakeState?.total_discrepancy),
    deltaCost: singleStocktakeState?.total_discrepancy_cost,
  };

  const cellClass = (column) =>
    [column.num ? "stp-num" : "", column.extraClass || ""]
      .filter(Boolean)
      .join(" ");

  return (
    <div className="stp-page" ref={printRef}>
      <div className="stp-header">
        <div className="stp-header__store">
          <p className="stp-store-name">{storenameCookie}</p>
          {storeAddress ? (
            <p className="stp-store-address">{storeAddress}</p>
          ) : null}
        </div>
        <div className="stp-header__meta">
          <p className="stp-title">Stocktake</p>
          <p className="stp-meta-line">
            Stocktake Number: {singleStocktakeState?.st_id}
          </p>
          <p className="stp-meta-line">
            Employee Name: {singleStocktakeState?.employee_name || "Admin"}
          </p>
        </div>
      </div>

      <table className="stp-table">
        <colgroup>
          {columns.map((column) => (
            <col
              key={column.key}
              style={{
                width: hideDiscrepancyCost ? column.widthNoCost : column.width,
              }}
            />
          ))}
        </colgroup>
        <thead>
          <tr className="stp-thead-spacer" aria-hidden="true">
            <th colSpan={columns.length} />
          </tr>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={cellClass(column)}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td className="stp-empty" colSpan={columns.length}>
                No stocktake items to print.
              </td>
            </tr>
          ) : (
            items.map((item, index) => {
              const cells = buildRowCells(item);
              return (
                <tr key={index}>
                  {columns.map((column) => (
                    <td key={column.key} className={cellClass(column)}>
                      {cells[column.key]}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
        <tfoot>
          <tr className="stp-total-row">
            {columns.map((column) => (
              <td key={column.key} className={cellClass(column)}>
                {totalCells[column.key] ?? ""}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default StocktakeReportPrint;
